import React, { useEffect, useRef } from 'react'

export type ActionPopupMenuItem<T> = {
  label: string
  className?: string
  disabled?: boolean | ((row: T) => boolean)
  onClick: (row: T) => void
  icon?: React.ReactNode
}

type ActionPopupMenuPosition = {
  top: number
  left: number
}

type ActionPopupMenuProps<T> = {
  row: T | null
  isOpen: boolean
  items: ActionPopupMenuItem<T>[]
  position: ActionPopupMenuPosition | null
  onClose: () => void
}

export const ActionPopupMenu = <T,>({
  row,
  isOpen,
  items,
  position,
  onClose
}: ActionPopupMenuProps<T>) => {
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen || !row || !position) {
    return null
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[180px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
      style={{
        top: position.top,
        left: position.left
      }}
    >
      {items.map((item) => {
        const isDisabled =
          typeof item.disabled === 'function'
            ? item.disabled(row)
            : item.disabled

        return (
          <button
            key={item.label}
            type="button"
            disabled={isDisabled}
            onClick={() => {
              item.onClick(row)
              onClose()
            }}
            className={`flex w-full items-center px-4 py-2 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-100 dark:hover:bg-gray-700 ${
              item.className ?? ''
            }`}
          >
            {item.icon && <span className="mr-2">{item.icon}</span>}
            {item.label}
          </button>
        )
      })}
    </div>
  )
}
